import { TruncatedTextWithTooltip } from '@app/components/base/TruncatedTextWithTooltip';
import { createActionsColumn } from '@app/modules/audit/components/create-actions-column';
import { createCustomIdColumn } from '@app/modules/audit/components/create-custom-id-column';
import { createStatusColumn } from '@app/modules/audit/components/create-status-column';
import { advertiserFieldMessage } from '@app/modules/audit/modules/station-audit/components/AdvertiserField';
import { campaignFieldMessage } from '@app/modules/audit/modules/station-audit/components/CampaignField';
import { orderFieldMessage } from '@app/modules/audit/modules/station-audit/components/OrderField';
import { ScheduleStatusReportItem } from '@mi-front/core';
import { MrtTable } from '@mi-front/ui';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { RowActionMui } from '../../../../../../../../components/table/RowActionMui';
import { RowActions } from '../../../../../../../../components/table/RowActions';
import { ReportResultProps } from '../../../../../../../../core/reports/types';
import { useActiveStation } from '../../../../../../../../core/stations/hooks/useActiveStation';
import { AuditMediaPlayerAction } from '../../../../components/AuditMediaPlayerAction';
import { useAuditSpotMatches } from '../../../../hooks/useAuditSpotMatches';
import { getScheduleStatusCounts } from './get-schedule-status-counts';
import {
  getScheduleStatusKind,
  getScheduleStatusSortRank,
  matchesScheduleStatusFilter,
  ScheduleStatusFilterValue,
} from './get-schedule-status-kind';
import { scheduleStatusHeaderMessage } from './schedule-status-spot-messages';
import { hasPossibleUnmatchedMatches, uniqueScheduleField } from './schedule-status-spot-utils';
import { ScheduleStatusSpotDetailDialog } from './ScheduleStatusSpotDetailDialog';
import { ScheduleStatusSpotOverview } from './ScheduleStatusSpotOverview';
import { ScheduleStatusSpotStatusChip } from './ScheduleStatusSpotStatusChip';
import { ScheduleStatusSpotReport } from './ScheduleStatusSpotView';

export function ScheduleStatusSpotResult(p: ReportResultProps<ScheduleStatusSpotReport>) {
  const { $t } = useIntl();
  const station = useActiveStation();
  const [detail, showDetail] = useState<ScheduleStatusReportItem | null>(null);
  const [statusFilter, setStatusFilter] = useState<ScheduleStatusFilterValue | null>(null);
  const statusCounts = useMemo(() => getScheduleStatusCounts(p.result.items), [p.result.items]);
  const visibleItems = useMemo(
    () => p.result.items.filter((item) => matchesScheduleStatusFilter(item.status, statusFilter)),
    [p.result.items, statusFilter],
  );

  const fetchUnmatchedMatches = detail != null && hasPossibleUnmatchedMatches(detail.status);
  const matchesQuery = useAuditSpotMatches(
    station,
    {
      from: p.values.from,
      to: p.values.to,
    },
    {
      customId: fetchUnmatchedMatches ? detail.customId : null,
    },
  );

  return (
    <>
      <ScheduleStatusSpotOverview
        counts={statusCounts}
        selected={statusFilter}
        onSelect={setStatusFilter}
      />
      <MrtTable
        data={visibleItems}
        enableTopToolbar={false}
        initialState={{
          sorting: [{ id: 'status', desc: false }],
        }}
        columns={[
          createCustomIdColumn<ScheduleStatusReportItem>($t, {
            accessor: (row) => row.customId,
          }),
          createStatusColumn<ScheduleStatusReportItem>($t, {
            header: scheduleStatusHeaderMessage,
            size: 220,
            enableSorting: true,
            accessorFn: (item) => getScheduleStatusKind(item.status),
            sortingFn: (rowA, rowB) =>
              getScheduleStatusSortRank(rowA.original.status) -
              getScheduleStatusSortRank(rowB.original.status),
            Cell: ({ row: { original: item } }) => (
              <ScheduleStatusSpotStatusChip
                status={item.status}
                missingAudio={!item.audio}
                onClick={() => showDetail(item)}
              />
            ),
          }),
          {
            id: 'advertiser',
            header: $t(advertiserFieldMessage),
            size: 170,
            enableSorting: true,
            accessorFn: (item) => uniqueScheduleField(item.schedules, 'advertiser'),
            Cell: ({ renderedCellValue }) => (
              <TruncatedTextWithTooltip value={renderedCellValue as string} />
            ),
          },
          {
            id: 'campaign',
            header: $t(campaignFieldMessage),
            size: 170,
            enableSorting: true,
            accessorFn: (item) => uniqueScheduleField(item.schedules, 'campaign'),
            Cell: ({ renderedCellValue }) => (
              <TruncatedTextWithTooltip value={renderedCellValue as string} />
            ),
          },
          {
            id: 'order',
            header: $t(orderFieldMessage),
            size: 100,
            enableSorting: true,
            accessorFn: (item) => uniqueScheduleField(item.schedules, 'order'),
            Cell: ({ renderedCellValue }) => (
              <TruncatedTextWithTooltip value={renderedCellValue as string} />
            ),
          },
          createActionsColumn<ScheduleStatusReportItem>($t, {
            Cell: ({ row }) => (
              <RowActions>
                <RowActionMui
                  title={
                    <FormattedMessage
                      id="audit.schedulesListScreen.viewScheduleBtn"
                      defaultMessage="Ver detalle"
                    />
                  }
                  onClick={() => showDetail(row.original)}
                >
                  <InfoIcon />
                </RowActionMui>

                {row.original.audio && (
                  <AuditMediaPlayerAction
                    audio={{
                      ...row.original.audio,
                      stationId: station.id,
                    }}
                  />
                )}
              </RowActions>
            ),
          }),
        ]}
      />
      <ScheduleStatusSpotDetailDialog
        item={detail}
        onHide={() => showDetail(null)}
        matchesQuery={matchesQuery}
      />
    </>
  );
}
